import { type FC, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchEvalReviewExport } from '@queries/evalConfigs';
import clsx from 'clsx';

import { buildCsvFilename, downloadCsv, toCsv } from '../utils/findingsCsv';

interface ExportReviewFeedbackButtonProps {
  /** The config whose runs to export. */
  configId: string;
  /** Names the downloaded file. */
  configName: string;
}

/**
 * Per-config review export, for the eval-configs table: every finding and
 * every piece of reviewer feedback across every contract this config was run
 * against — the shape the offline eval loop's triage pass reads. Config by
 * config on purpose: the champion answers "what have brokers seen", a
 * challenger scores an experiment round, and mixing configs in one file
 * muddles both questions.
 */
export const ExportReviewFeedbackButton: FC<ExportReviewFeedbackButtonProps> = ({ configId, configName }) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const rows = await fetchEvalReviewExport({ configId });
      if (rows.length === 0) {
        toast.info('No runs to export for this config yet', { autoClose: 4_000 });
        return;
      }
      downloadCsv(buildCsvFilename(configName, new Date().toISOString().split('T')[0]), toCsv(rows));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to export the review feedback');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={isExporting}
      data-testid={`eval-config-export-runs-${configId}`}
      title="Download this config's runs — findings, review state and feedback — as CSV"
      className={clsx(
        'text-primary-600 hover:text-primary-900 cursor-pointer font-light transition-colors',
        isExporting && 'cursor-not-allowed opacity-50',
      )}
    >
      {isExporting ? 'Exporting…' : 'Export runs'}
    </button>
  );
};
